import Cookies from "js-cookie";
import * as Sentry from "@sentry/browser";

import getAuthCookie from "./authCookieParser";
import { send } from "./fetch";

export class FetchServerContextError extends Error {
}

export class FetchInAppServerContextError extends Error {
}

/**
 * Validates the server context. Returns false if the context is undefined, empty or does not have the critical properties;
 * @param {object} context
 */
export function isServerContextValid(context) {
  const criticalProperties = ["user_id"];
  if (
    !context ||
    (Object.keys(context).length === 0 && context.constructor === Object)
  ) {
    return false;
  }
  return !criticalProperties.some(
    property => !context[property] || context[property] === "undefined"
  );
}

/**
 * Look through serverContext and if certain fields are found, set them in
 * the cookie.
 * @param {Object} serverContext
 */
function setCookiesAndReturnContext(serverContext) {
  if (serverContext.route_id) {
    Cookies.set("_routeId", serverContext.route_id, {
      domain: serverContext.session_cookie_domain,
    });
  }

  if (
    serverContext.campaign_promo_cookie &&
    serverContext.campaign_promo_cookie.active
  ) {
    Cookies.set("campaign_promo", serverContext.campaign_promo_cookie.value, {
        domain: serverContext.session_cookie_domain,
      }
    );
  } else {
    Cookies.remove("campaign_promo");
  }
  return serverContext;
}

/**
 * Performs the fetch request to the appropriate url.
 * @param {string} URL
 */
function fetchContext(URL) {
  return send("GET", URL)
    .then(context => {
      if (!isServerContextValid(context)) {
        throw Error(`Invalid context: ${JSON.stringify(context)}`);
      }
      return setCookiesAndReturnContext(context);
    })
    .catch(error => {
      // Had to add Sentry here too because its initialization is after
      // This can be refactored in the future
      const SENTRY_SAMPLE_RATE = 100;
      Sentry.init({
        dsn: __SENTRY_PROJECT_DSN_JS__,
        shouldSendCallback() {
          const sampleRate = SENTRY_SAMPLE_RATE;
          return Math.random() * 100 <= sampleRate;
        },
      });

      Sentry.withScope(scope => {
        // NOTE(Paul): change the fingerprint to track it as new sentry error
        scope.setFingerprint([
          "GET",
          "landing page server context fetch detailed",
        ]);

        // Error thrown by the send utility has an error key.
        if (error.error) scope.setExtra("error", error.error);

        // NOTE(Paul): the error can come from either network error (when the eror has only the message: TypeError: Failed to fetch)
        // or throw in the then when it has the response
        if (error.statusText) {
          scope.setExtra("Response extra", {
            headers: error.headers,
            ok: false,
            redirected: error.redirected,
            statusText: error.statusText,
            status: error.status,
            type: error.type,
          });
          scope.setExtra("URL", error.url);

          Sentry.captureException(error.statusText);
        } else {
          scope.setExtra("URL", URL);
          Sentry.captureException(error);
        }
      });
      throw new FetchServerContextError();
    });
}

/**
 * Fetch the serverContext of In-app-upsells from the Growth API.
 */
function fetchInAppContext(URL) {
  const authCookie = getAuthCookie();
  return send("POST", URL, authCookie)
    .then(context => {
      if (!isServerContextValid(context)) {
        throw Error(`Invalid context: ${JSON.stringify(context)}`);
      }
      return setCookiesAndReturnContext(context);
    })
    .catch(error => {
      // Had to add Sentry here too because its initialization is after
      // This can be refactored in the future
      const SENTRY_SAMPLE_RATE = 100;
      Sentry.init({
        dsn: __SENTRY_PROJECT_DSN_JS__,
        shouldSendCallback() {
          const sampleRate = SENTRY_SAMPLE_RATE;
          return Math.random() * 100 <= sampleRate;
        },
      });

      Sentry.withScope(scope => {
        // NOTE(Paul): change the fingerprint to track it as new sentry error
        scope.setFingerprint([
          "POST",
          "in-app-upsells server context fetch detailed",
        ]);
        // NOTE(Paul): the error can come from either network error (when the eror has only the message
        // "TypeError: Failed to fetch") or throw in the then when it has the response
        if (error.statusText) {
          scope.setExtra("Response extra", {
            headers: error.header,
            ok: error.ok,
            redirected: error.redirected,
            status: error.status,
            statusText: error.statusText,
            type: error.type,
          });
          scope.setExtra("URL", error.url);

          Sentry.captureException(error.statusText);
        } else {
          scope.setExtra("URL", URL);
          Sentry.captureException(error);
        }
      });
      throw new FetchInAppServerContextError();
    });
}

/**
 * Fetch the serverContext from the Growth API.
 * Currently has special handling in app context fetching.
 */
export default function getContext() {
  let { pathname } = window.location;
  const { search } = window.location;

  if (pathname.startsWith("/")) {
    pathname = pathname.slice(1, pathname.length);
  }
  if (pathname.endsWith("/")) {
    pathname = pathname.slice(0, pathname.length - 1);
  }
  // Use v2 routing for /add-ons, /offers, and /in-app-offers
  if (["add-ons", "offers"].some(str => pathname.includes(str))) {
    pathname += "/v2";
  }

  const pathParts = [];
  if (pathname !== "") {
    pathParts.push(pathname);
  }
  if (search !== "") {
    pathParts.push(search);
  }

  const URL = `/api/context/v2/${pathParts.join("/")}`;

  // NOTE(sumin): Hacky way to call the POST request version only for in-app-offers users.
  if (pathname.includes("in-app-offers") || pathname.includes("course")) {
    return fetchInAppContext(URL);
  }
  return fetchContext(URL);
}
